import React, { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [editingRole, setEditingRole] = useState(null); // role object being edited/created
  const [selectedPerms, setSelectedPerms] = useState([]); // permission IDs
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const fetchData = async () => {
    try {
      const [rolesData, permsData] = await Promise.all([
        apiJson("/api/admin/roles"),
        apiJson("/api/admin/roles/permissions"),
      ]);
      setRoles(rolesData);
      setPermissions(permsData);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const groupedPermissions = permissions.reduce((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module].push(p);
    return acc;
  }, {});

  const moduleLabels = { master: "Master Data", admissions: "Admissions", system: "System" };

  const startCreate = () => {
    setEditingRole({ id: null });
    setFormName("");
    setFormDesc("");
    setSelectedPerms([]);
    setMessage("");
  };

  const startEdit = (role) => {
    setEditingRole(role);
    setFormName(role.name);
    setFormDesc(role.description || "");
    setSelectedPerms(role.permissions.map((rp) => rp.permission.id));
    setMessage("");
  };

  const cancelEdit = () => {
    setEditingRole(null);
    setMessage("");
  };

  const togglePerm = (permId) => {
    setSelectedPerms((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const toggleModule = (moduleName) => {
    const modulePermIds = groupedPermissions[moduleName].map((p) => p.id);
    const allSelected = modulePermIds.every((id) => selectedPerms.includes(id));
    if (allSelected) {
      setSelectedPerms((prev) => prev.filter((id) => !modulePermIds.includes(id)));
    } else {
      setSelectedPerms((prev) => [...new Set([...prev, ...modulePermIds])]);
    }
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setMessage("Role name is required");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      if (editingRole.id) {
        await apiJson(`/api/admin/roles/${editingRole.id}`, {
          method: "PUT",
          body: JSON.stringify({ name: formName, description: formDesc, permissionIds: selectedPerms }),
        });
      } else {
        await apiJson("/api/admin/roles", {
          method: "POST",
          body: JSON.stringify({ name: formName, description: formDesc, permissionIds: selectedPerms }),
        });
      }
      setEditingRole(null);
      await fetchData();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await apiJson(`/api/admin/roles/${role.id}`, { method: "DELETE" });
      await fetchData();
    } catch (err) {
      setMessage(err.message);
    }
  };

  if (loading) return <p className="p-4 text-gray-500">Loading...</p>;

  // Edit/Create form
  if (editingRole) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {editingRole.id ? "Edit Role" : "Create Role"}
        </h1>

        {message && <p className="text-red-600 text-sm mb-3">{message}</p>}

        <div className="bg-white shadow rounded-lg p-4 mb-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Role Name</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              disabled={editingRole.isSystem}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Permissions</h2>
          {Object.entries(groupedPermissions).map(([mod, perms]) => {
            const allChecked = perms.every((p) => selectedPerms.includes(p.id));
            const someChecked = perms.some((p) => selectedPerms.includes(p.id));
            return (
              <div key={mod} className="mb-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => toggleModule(mod)}
                    className="rounded"
                  />
                  {moduleLabels[mod] || mod}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 ml-6">
                  {perms.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPerms.includes(p.id)}
                        onChange={() => togglePerm(p.id)}
                        className="rounded"
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={cancelEdit}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Role list
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Roles & Permissions</h1>
        <button
          onClick={startCreate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700"
        >
          + New Role
        </button>
      </div>

      {message && <p className="text-red-600 text-sm mb-3">{message}</p>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-700">Role</th>
              <th className="px-4 py-3 font-medium text-gray-700">Description</th>
              <th className="px-4 py-3 font-medium text-gray-700">Users</th>
              <th className="px-4 py-3 font-medium text-gray-700">Permissions</th>
              <th className="px-4 py-3 font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">
                  {role.name}
                  {role.isSystem && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                      system
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{role.description}</td>
                <td className="px-4 py-3 text-gray-600">{role._count?.admins || 0}</td>
                <td className="px-4 py-3 text-gray-600">{role.permissions.length}</td>
                <td className="px-4 py-3 space-x-2">
                  <button
                    onClick={() => startEdit(role)}
                    className="text-indigo-600 hover:underline text-sm"
                  >
                    Edit
                  </button>
                  {!role.isSystem && (
                    <button
                      onClick={() => handleDelete(role)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoleManagement;
